<script setup>
import { ref, nextTick } from 'vue';

defineProps({
  isSending: { type: Boolean, default: false },
});

const emit = defineEmits(['send']);

const messageText = ref('');
const textareaRef = ref(null);
const isFocused = ref(false);

const resizeTextarea = () => {
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = '24px';
      textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 200)}px`;
    }
  });
};

const handleSend = () => {
  const content = messageText.value.trim();
  if (!content) return;
  emit('send', content);
  messageText.value = '';
  nextTick(() => {
    if (textareaRef.value) textareaRef.value.style.height = '24px';
  });
};

const handleKeydown = event => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};
</script>

<template>
  <div class="relative px-4 pb-4 pt-2">
    <div
      class="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-n-background to-transparent"
    />

    <div class="mx-auto max-w-3xl">
      <div
        class="relative overflow-hidden rounded-2xl transition-shadow duration-200"
        :class="[
          isFocused
            ? 'shadow-[0_0_0_1px_rgba(139,92,246,0.3),0_2px_12px_rgba(139,92,246,0.08)]'
            : 'shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_1px_4px_rgba(0,0,0,0.1)]',
        ]"
      >
        <div class="bg-n-alpha-2">
          <div class="flex items-end gap-1 px-4 pb-3 pt-3">
            <textarea
              id="ai-chat-input"
              ref="textareaRef"
              v-model="messageText"
              class="reset-base max-h-[200px] min-h-[24px] flex-1 resize-none !border-0 !bg-transparent p-0 text-sm leading-6 text-n-slate-12 !shadow-none !outline-none !ring-0 placeholder:text-n-slate-10"
              placeholder="Digite sua mensagem..."
              rows="1"
              :disabled="isSending"
              @input="resizeTextarea"
              @keydown="handleKeydown"
              @focus="isFocused = true"
              @blur="isFocused = false"
            />
          </div>

          <div class="flex items-center justify-between px-3 pb-2">
            <div class="flex items-center gap-0.5">
              <span class="px-2 py-1 text-[10px] text-n-slate-9">
                <kbd class="rounded bg-n-alpha-2 px-1 py-px font-mono text-[10px] text-n-slate-9">Enter</kbd>
                enviar
                <span class="mx-1 text-n-slate-7">·</span>
                <kbd class="rounded bg-n-alpha-2 px-1 py-px font-mono text-[10px] text-n-slate-9">Shift+Enter</kbd>
                nova linha
              </span>
            </div>
            <button
              id="ai-chat-send-btn"
              class="flex size-8 items-center justify-center rounded-xl transition-all duration-200"
              :class="[
                messageText.trim()
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-sm hover:shadow-md hover:brightness-110 active:scale-95'
                  : 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed',
              ]"
              :disabled="isSending || !messageText.trim()"
              @click="handleSend"
            >
              <span
                v-if="isSending"
                class="i-lucide-loader-2 animate-spin text-sm"
              />
              <span v-else class="i-lucide-arrow-up text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
